import React, { useRef, useState } from 'react';
import { uploadImage, fullUrl, listFiles } from './adminApi';

// Drag-and-drop (or click) image uploader, with a reusable image library picker.
export default function DragDropImage({ value, onChange, label, testid, heightClass = 'h-40' }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [libOpen, setLibOpen] = useState(false);
  const [lib, setLib] = useState([]);
  const [libBusy, setLibBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setErr('Please choose an image file'); return; }
    setErr(''); setBusy(true);
    try { const r = await uploadImage(file); onChange(r.url); }
    catch { setErr('Upload failed, try again'); }
    finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const openLibrary = async () => {
    setLibOpen(true); setLibBusy(true);
    try { setLib(await listFiles()); } catch { setLib([]); } finally { setLibBusy(false); }
  };

  return (
    <div>
      {label && <p className="text-xs font-semibold uppercase tracking-wide mb-2">{label}</p>}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        data-testid={testid}
        className={`relative ${heightClass} w-full cursor-pointer rounded-2xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-colors ${
          drag ? 'border-[#2C3A28] bg-[#2C3A28]/5' : 'border-[#2C3A28]/25 bg-white hover:border-[#2C3A28]/50'
        }`}
      >
        {value ? (
          <>
            <img src={fullUrl(value)} alt="preview" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
              <span className="text-white text-sm font-semibold">Drop or click to replace</span>
            </div>
          </>
        ) : (
          <div className="text-center px-4 text-[#2C3A28]/60">
            <p className="text-sm font-semibold">Drag &amp; drop an image here</p>
            <p className="text-xs mt-1">or click to browse</p>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <span className="text-sm font-semibold text-[#2C3A28]">Uploading…</span>
          </div>
        )}
      </div>

      <div className="mt-2 flex items-center gap-4">
        <button type="button" onClick={(e) => { e.stopPropagation(); openLibrary(); }} data-testid={testid ? `${testid}-library` : undefined}
          className="text-xs font-semibold text-[#2C3A28] hover:underline">Choose from library</button>
        {value && (
          <button type="button" onClick={(e) => { e.stopPropagation(); onChange(''); }}
            className="text-xs font-semibold text-red-700 hover:underline">Reset to default</button>
        )}
      </div>
      {err && <p className="mt-1 text-xs text-red-700">{err}</p>}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />

      {libOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="image-library-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setLibOpen(false)} />
          <div className="relative w-full max-w-3xl max-h-[85vh] overflow-y-auto bg-white rounded-2xl p-5 shadow-2xl">
            <div className="flex items-center justify-between sticky top-0 bg-white pb-3">
              <h3 className="font-display text-xl font-semibold text-[#2C3A28]">Image library</h3>
              <button onClick={() => setLibOpen(false)} className="text-sm font-semibold text-[#2C3A28] hover:underline">Close</button>
            </div>
            <p className="text-xs text-[#2C3A28]/60 mb-3">Click any image to use it in this slot. Upload new images by dragging onto a slot.</p>
            {libBusy ? (
              <p className="text-sm text-[#2C3A28]/60 py-8 text-center">Loading…</p>
            ) : lib.length === 0 ? (
              <p className="text-sm text-[#2C3A28]/60 py-8 text-center">No uploads yet. Drag a photo onto a slot to add it to your library.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {lib.map((f) => (
                  <button key={f.path} type="button" onClick={() => { onChange(f.url); setLibOpen(false); }}
                    data-testid="library-image-item"
                    className="group relative aspect-square rounded-xl overflow-hidden border border-[#2C3A28]/10 hover:border-[#2C3A28] transition-colors">
                    <img src={fullUrl(f.url)} alt="library" className="w-full h-full object-cover" />
                    <span className="absolute inset-0 bg-[#2C3A28]/0 group-hover:bg-[#2C3A28]/30 transition-colors" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
